import { Archive, CloudUpload } from 'lucide-react'
import { Suspense, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { Muted } from '@/components/sv/typographies'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { objectStorageLocations } from '@/config/object-storage-locations'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import {
  EXTERNAL_BACKUP_RETENTION_DAYS,
  getSuggestedObjectStorageName,
} from '@/routes/databases/create-database/form/sections/external-backups/constants'
import { ExternalBackupsFallback } from '@/routes/databases/create-database/form/sections/external-backups/fallback'
import { CREATE_DATABASE_OBJECT_STORAGES_QUERY } from '@/routes/databases/create-database/form/sections/external-backups/query'
import type { CreateDatabaseFormValues } from '@/routes/databases/create-database/form/schema'
import type {
  CreateDatabaseObjectStoragesQuery,
  CreateDatabaseObjectStoragesQueryVariables,
} from '@/routes/databases/create-database/form/sections/external-backups/query.types.gen'

const storageModeCardClassName = cn(
  'relative flex cursor-pointer flex-col items-start gap-1 rounded-lg border p-4 pl-10',
  'has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
  'has-[[data-disabled]]:cursor-not-allowed has-[[data-disabled]]:opacity-60'
)

const ExternalBackupsContent = () => {
  const { idCompany } = useIdCompany()
  const form = useFormContext<CreateDatabaseFormValues>()
  const { control, watch, setValue, resetField, formState } = form

  const { data } = useSuspenseQuery<
    CreateDatabaseObjectStoragesQuery,
    CreateDatabaseObjectStoragesQueryVariables
  >(CREATE_DATABASE_OBJECT_STORAGES_QUERY, { variables: { idCompany } })

  const objectStorages = (data?.company?.objectStorages?.items ?? []).filter(
    (objectStorage) => objectStorage.status === 'active'
  )
  const hasObjectStorages = objectStorages.length > 0
  const [firstObjectStorage] = objectStorages

  const dbName = watch('data.dbName')
  const isEnabled = watch('externalBackup.isEnabled')
  const storageMode = watch('externalBackup.storageMode')
  const isStorageNameDirty = Boolean(
    formState.dirtyFields.externalBackup?.objectStorageDisplayName
  )

  useEffect(() => {
    return () => {
      resetField('externalBackup')
    }
  }, [resetField])

  useEffect(() => {
    setValue('externalBackup.idObjectStorage', firstObjectStorage?.id ?? '')
  }, [firstObjectStorage, setValue])

  useEffect(() => {
    if (!isStorageNameDirty) {
      setValue(
        'externalBackup.objectStorageDisplayName',
        getSuggestedObjectStorageName(dbName)
      )
    }
  }, [dbName, isStorageNameDirty, setValue])

  return (
    <div className="rounded-lg border">
      <FormField
        control={control}
        name="externalBackup.isEnabled"
        render={({ field: { value, onChange, ...field } }) => (
          <FormItem className="flex items-center justify-between gap-4 p-4">
            <div className="flex flex-col gap-1">
              <FormLabel className="cursor-pointer">
                <CloudUpload className="size-4" aria-hidden="true" />
                External backups
              </FormLabel>
              <FormDescription>
                Store a daily dump backup in a Sevalla object storage and keep
                the last {EXTERNAL_BACKUP_RETENTION_DAYS} days.
              </FormDescription>
            </div>
            <FormControl>
              <Switch
                checked={Boolean(value)}
                onCheckedChange={onChange}
                aria-label="Toggle external backups"
                {...field}
              />
            </FormControl>
          </FormItem>
        )}
      />
      <Collapsible open={isEnabled}>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <Separator />
          <div className="space-y-4 p-4">
            <FormField
              control={control}
              name="externalBackup.storageMode"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Backup destination</FormLabel>
                  <FormControl>
                    <RadioGroup
                      className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                      value={value}
                      onValueChange={onChange}
                    >
                      <Label className={storageModeCardClassName}>
                        <RadioGroupItem
                          value="existing"
                          id="external-backup-storage-existing"
                          disabled={!hasObjectStorages}
                          className="absolute top-4.5 left-4 data-[state=checked]:border-primary"
                        />
                        <div className="font-medium">
                          Existing object storage
                        </div>
                        <Muted>
                          {hasObjectStorages
                            ? 'Back up into one of your object storages.'
                            : 'No object storage in this company yet.'}
                        </Muted>
                      </Label>
                      <Label className={storageModeCardClassName}>
                        <RadioGroupItem
                          value="new"
                          id="external-backup-storage-new"
                          className="absolute top-4.5 left-4 data-[state=checked]:border-primary"
                        />
                        <div className="font-medium">New object storage</div>
                        <Muted>Create one alongside the database.</Muted>
                      </Label>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            {storageMode === 'existing' ? (
              <FormField
                control={control}
                name="externalBackup.idObjectStorage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Object storage</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger
                          className="w-full"
                          aria-label="Select object storage"
                        >
                          <SelectValue placeholder="Select an object storage" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {objectStorages.map((objectStorage) => (
                          <SelectItem
                            key={objectStorage.id}
                            value={objectStorage.id}
                          >
                            <Archive className="size-4" aria-hidden="true" />
                            {objectStorage.displayName}
                            <span className="text-muted-foreground">
                              {objectStorageLocations[objectStorage.location]}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={control}
                name="externalBackup.objectStorageDisplayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Object storage name</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        aria-label="Object storage name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      The object storage is created together with the database,
                      in the same project.
                    </FormDescription>
                  </FormItem>
                )}
              />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

export const ExternalBackups = () => {
  return (
    <Suspense fallback={<ExternalBackupsFallback />}>
      <ExternalBackupsContent />
    </Suspense>
  )
}
